import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, EvidenceBadge } from "@/lib/api";
import { toast } from "sonner";
import { Wand2, Send, Camera as CamIcon, RefreshCcw, Trash2 } from "lucide-react";

export default function ScriptStudio() {
  const { id } = useParams();
  const [scripts, setScripts] = useState([]);
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState(false);
  const [shotList, setShotList] = useState(null);

  const loadList = () => api.get("/scripts").then((r) => setScripts(r.data));

  useEffect(() => {
    loadList();
    if (id) load(id);
  }, [id]);

  const load = async (sid) => {
    const { data } = await api.get(`/scripts/${sid}`);
    setActive(data);
    const sl = await api.get(`/shot_lists/by_script/${sid}`);
    setShotList(sl.data?.id ? sl.data : null);
  };

  const submitForReview = async () => {
    await api.patch(`/scripts/${active.id}`, { approval_status: "in_review" });
    toast.success("Submitted for founder approval");
    load(active.id);
  };

  const deleteScript = async () => {
    if (!confirm(`Delete script "${active.title}"? This also removes its shot list.`)) return;
    await api.delete(`/scripts/${active.id}`);
    toast.success("Deleted");
    setActive(null);
    loadList();
  };

  const approve = async (decision) => {
    await api.post("/approvals", { target_type: "script", target_id: active.id, target_version: active.version, decision, comment: "" });
    toast.success("Decision recorded");
    load(active.id);
  };

  const genShotList = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/ai/generate_shot_list/${active.id}`);
      setShotList(data);
      toast.success(`Shot list ready · ${data.items.length} shots`);
    } catch (e) { toast.error("Shot list gen failed"); }
    finally { setBusy(false); }
  };

  const updateField = async (field, value) => {
    const upd = { [field]: value };
    const { data } = await api.patch(`/scripts/${active.id}`, upd);
    setActive(data);
  };

  return (
    <div className="p-8 max-w-[1600px]">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-1">Script Studio</div>
        <h1 className="text-5xl font-black tracking-tight leading-none" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>Scripts</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
        <div className="bg-white border border-neutral-200 rounded-lg p-4 h-fit">
          <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-3">All scripts</div>
          <div className="space-y-2">
            {scripts.map((s) => (
              <button key={s.id} onClick={() => load(s.id)} data-testid={`script-item-${s.id}`}
                className={`w-full text-left p-2 rounded transition-colors ${active?.id === s.id ? "bg-neutral-100" : "hover:bg-neutral-50"}`}>
                <div className="text-sm font-medium truncate">{s.title}</div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-0.5">v{s.version} · {s.approval_status}</div>
              </button>
            ))}
            {scripts.length === 0 && <div className="text-xs text-neutral-500">Generate an idea first, then convert to script.</div>}
          </div>
        </div>

        {active ? (
          <div className="space-y-6">
            <div className="bg-white border border-neutral-200 rounded-lg p-6">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <h2 className="text-3xl font-black tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>{active.title}</h2>
                  <div className="text-xs text-neutral-500 mt-1">Version {active.version} · {active.duration_seconds}s · {active.approval_status}</div>
                </div>
                <div className="flex gap-2">
                  {active.approval_status === "draft" && <button data-testid="submit-review-btn" onClick={submitForReview} className="text-xs font-semibold px-3 py-2 rounded border border-neutral-300 flex items-center gap-1 hover:bg-neutral-50"><Send className="w-3 h-3" />Submit for review</button>}
                  {active.approval_status !== "approved" && <button data-testid="approve-btn" onClick={() => approve("approved")} className="text-xs font-semibold px-3 py-2 rounded text-neutral-900" style={{ background: "#D4FF00" }}>Approve</button>}
                  {active.approval_status !== "approved" && <button data-testid="request-changes-btn" onClick={() => approve("changes_requested")} className="text-xs font-semibold px-3 py-2 rounded border border-neutral-300 hover:bg-neutral-50">Request changes</button>}
                  <button data-testid="delete-script-btn" onClick={deleteScript} className="text-xs font-semibold px-3 py-2 rounded border border-red-300 text-red-700 hover:bg-red-50 flex items-center gap-1"><Trash2 className="w-3 h-3" />Delete</button>
                </div>
              </div>
              <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-1">Hook</div>
              <input data-testid="hook-input" value={active.selected_hook || ""} onChange={(e) => setActive({ ...active, selected_hook: e.target.value })}
                onBlur={(e) => updateField("selected_hook", e.target.value)}
                className="w-full text-lg font-bold border-b border-neutral-200 pb-2 mb-4 focus:outline-none focus:border-neutral-900" />

              <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-1">Spoken script</div>
              <textarea data-testid="spoken-script" value={active.spoken_script || ""} onChange={(e) => setActive({ ...active, spoken_script: e.target.value })}
                onBlur={(e) => updateField("spoken_script", e.target.value)}
                rows={10} className="w-full border border-neutral-200 rounded p-3 text-sm focus:outline-none focus:border-neutral-900" />

              <div className="grid grid-cols-2 gap-4 mt-4">
                <div>
                  <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-1">Caption</div>
                  <textarea value={active.caption || ""} onChange={(e) => setActive({ ...active, caption: e.target.value })} onBlur={(e) => updateField("caption", e.target.value)}
                    rows={4} className="w-full border border-neutral-200 rounded p-2 text-sm focus:outline-none focus:border-neutral-900" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-1">CTA</div>
                  <input value={active.cta || ""} onChange={(e) => setActive({ ...active, cta: e.target.value })} onBlur={(e) => updateField("cta", e.target.value)}
                    className="w-full border border-neutral-200 rounded p-2 text-sm focus:outline-none focus:border-neutral-900" />
                  <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold mt-3 mb-1">Claim review</div>
                  <div><EvidenceBadge confidence={active.claim_review === "clear" ? "high" : "medium"} /> <span className="text-xs text-neutral-600 ml-1">{active.claim_review}</span></div>
                </div>
              </div>

              {active.beats?.length > 0 && (
                <div className="mt-6">
                  <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-2">Beats</div>
                  <div className="space-y-2">
                    {active.beats.map((b, idx) => (
                      <div key={idx} className="border-l-2 border-neutral-900 pl-3">
                        <div className="text-xs uppercase tracking-widest text-neutral-500">{b.label} · {b.seconds}s</div>
                        <div className="text-sm">{b.text}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {active.flagged_sentences?.length > 0 && (
                <div className="mt-6 border border-orange-300 bg-orange-50 rounded p-3">
                  <div className="text-xs font-bold text-orange-900 uppercase tracking-widest mb-1">Flagged for claim review</div>
                  {active.flagged_sentences.map((f, i) => (
                    <div key={i} className="text-xs text-orange-900 mt-1"><span className="font-semibold">"{f.sentence}"</span> — {f.reason}</div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-white border border-neutral-200 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold">Shot list</div>
                <button data-testid="gen-shot-list-btn" onClick={genShotList} disabled={busy}
                  className="text-xs font-semibold px-3 py-2 rounded flex items-center gap-1 text-neutral-900 disabled:opacity-50" style={{ background: "#D4FF00" }}>
                  {busy ? <RefreshCcw className="w-3 h-3 animate-spin" /> : <CamIcon className="w-3 h-3" />} Generate shot list
                </button>
              </div>
              {shotList ? (
                <div className="space-y-2">
                  <div className="text-xs text-neutral-600">Location: <span className="font-semibold">{shotList.consolidated_location || "mixed"}</span></div>
                  {shotList.items.map((it) => (
                    <div key={it.shot_number} className="border border-neutral-200 rounded p-3">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded bg-neutral-900 text-white text-xs font-bold flex items-center justify-center">{it.shot_number}</div>
                        <div className="text-sm font-semibold">{it.shot_type} · {it.location}</div>
                        <div className="text-xs text-neutral-500">· {it.duration_seconds}s</div>
                      </div>
                      <div className="text-xs text-neutral-600 mt-2"><span className="uppercase font-semibold tracking-wider">Audio:</span> {it.spoken_audio}</div>
                      {it.broll_action && <div className="text-xs text-neutral-600"><span className="uppercase font-semibold tracking-wider">B-roll:</span> {it.broll_action}</div>}
                      {it.on_screen_text && <div className="text-xs text-neutral-600"><span className="uppercase font-semibold tracking-wider">On-screen:</span> {it.on_screen_text}</div>}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-neutral-500">No shot list yet.</div>
              )}
            </div>
          </div>
        ) : (
          <div className="bg-white border border-dashed border-neutral-300 rounded-lg p-16 text-center text-neutral-500 text-sm">Pick a script from the left or generate one from an idea.</div>
        )}
      </div>
    </div>
  );
}
